import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAllLocations } from '../hooks/useAllLocations';
import { useMyProfile } from '../hooks/useMyProfile';
import { useDealCandidates, useResolveCandidate, type CandidateStatus, type DealCandidate } from '../hooks/useTripContinuity';
import { inputClass } from '../components/trips/TripControls';
import { formatCurrency, formatDate } from '../lib/formatters';
import { describeJobError, invokeTriggerTask } from '../lib/triggerTask';
import { canSetBillingTerms, toNumber } from '../lib/tripLifecycle';

const TABS: { key: CandidateStatus; label: string; hint: string }[] = [
  { key: 'needs_review', label: 'Needs review', hint: 'Looks like a visit, but the deal alone does not say where or when. Choose the property and dates, or ignore it.' },
  { key: 'imported', label: 'Imported', hint: 'Now completed trips in PazPortal, billed outside it (reconcile them on Trip Billing → Imported history).' },
  { key: 'ready', label: 'Queued', hint: 'Clear visits that import on the next run.' },
  { key: 'skipped', label: 'Not visits', hint: 'Mention a visit but are not one: remote support, lost deals, personal trips, deals still open.' },
  { key: 'ignored', label: 'Ignored', hint: 'A person decided these are not trips.' },
];

const btn = 'rounded px-3 py-1.5 min-h-[40px] text-xs font-medium disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary';

/**
 * Trip history from Zoho CRM Deals. The zoho-deal-trip-import task reads deals
 * (never writes them) and imports only the unambiguous ones; this page is where
 * a person settles the rest, so no visit is guessed onto the wrong property.
 */
export function TripImportsPage() {
  const { data, isLoading, isError } = useDealCandidates();
  const { data: me } = useMyProfile();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('view') as CandidateStatus | null) ?? 'needs_review';
  const [running, setRunning] = useState<string | null>(null);
  const rows = useMemo(() => data ?? [], [data]);
  const counts = useMemo(() => {
    const c: Record<CandidateStatus, number> = { ready: 0, needs_review: 0, imported: 0, ignored: 0, skipped: 0 };
    for (const r of rows) c[r.status] += 1;
    return c;
  }, [rows]);
  const meta = TABS.find((t) => t.key === tab) ?? TABS[0];
  const visible = rows.filter((r) => r.status === meta.key);
  const mayResolve = canSetBillingTerms(me?.role);

  const runNow = async () => {
    setRunning('Starting the import…');
    try {
      await invokeTriggerTask('zoho-deal-trip-import', {});
      setRunning('Import started. Refresh in a minute to see the results.');
    } catch (e) {
      setRunning(describeJobError(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-text">Trip Imports</h1>
          <p className="text-sm text-text-muted mt-0.5">
            Past visits read from Zoho CRM deals every morning. Zoho is only read, never written to.
          </p>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-1">
          <button type="button" onClick={runNow} className={`${btn} bg-surface-3 hover:bg-surface-strong text-text`}>Run import now</button>
          {running && <p className="text-xs text-text-muted max-w-xs" role="status">{running}</p>}
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto max-w-full" role="tablist" aria-label="Import status">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set('view', t.key);
              setParams(next, { replace: true });
            }}
            className={`px-3 py-1.5 min-h-[44px] sm:min-h-0 text-xs font-medium rounded-md whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
              tab === t.key ? 'bg-primary text-text-inverse' : 'bg-surface-3 text-text-muted hover:bg-surface-strong'
            }`}
          >
            {t.label}
            <span className={`ml-1.5 rounded-full px-1.5 ${tab === t.key ? 'bg-surface/20' : t.key === 'needs_review' && counts[t.key] > 0 ? 'bg-warning-soft text-warning' : 'bg-surface'}`}>
              {isLoading ? '…' : counts[t.key]}
            </span>
          </button>
        ))}
      </div>
      <p className="text-sm text-text-muted">{meta.hint}</p>

      {isLoading ? (
        <div className="rounded-lg border border-border bg-surface p-6 text-sm text-text-muted" role="status">Loading imports…</div>
      ) : isError ? (
        <div role="alert" className="rounded-lg border border-border bg-surface p-6 text-sm text-notification">Could not load imports. Refresh to try again.</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-border bg-surface p-6 text-center text-sm text-text-muted">
          Nothing imported yet. The import runs every morning; use "Run import now" to start it.
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-lg border border-border bg-surface p-6 text-center text-sm text-text-muted">Nothing here.</div>
      ) : (
        <ul className="space-y-2">
          {visible.map((c) => (
            <CandidateRow key={c.zoho_deal_id} c={c} mayResolve={mayResolve} />
          ))}
        </ul>
      )}
    </div>
  );
}

function CandidateRow({ c, mayResolve }: { c: DealCandidate; mayResolve: boolean }) {
  const [deciding, setDeciding] = useState(false);
  const resolve = useResolveCandidate();
  const amt = toNumber(c.amount);

  return (
    <li className="rounded-lg border border-border bg-surface p-3 sm:p-4 space-y-2">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div className="min-w-0">
          <a
            href={`https://crm.zoho.com/crm/tab/Potentials/${c.zoho_deal_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-primary hover:underline break-words"
          >
            {c.deal_name}
          </a>
          <p className="text-xs text-text-muted">
            {c.account_name ?? 'No account'}
            {c.stage ? ` · ${c.stage}` : ''}
            {c.closing_date ? ` · closed ${formatDate(c.closing_date)}` : ''}
            {amt != null ? ` · ${formatCurrency(Math.round(amt * 100))}` : ''}
          </p>
          {c.visit_start && (
            <p className="text-xs text-text-faint">
              Visit read as {formatDate(c.visit_start)}
              {c.visit_end && c.visit_end !== c.visit_start ? ` → ${formatDate(c.visit_end)}` : ''}
            </p>
          )}
        </div>
        {c.trip_id && (
          <Link to={`/trips?trip=${c.trip_id}`} className="shrink-0 text-xs font-medium text-primary hover:underline">Open trip</Link>
        )}
      </div>

      {c.reasons.length > 0 && (
        <ul className={`rounded px-3 py-2 text-xs space-y-0.5 ${c.status === 'needs_review' ? 'border border-warning bg-warning-soft text-warning' : 'bg-surface-3 text-text-muted'}`}>
          {c.reasons.map((r) => <li key={r}>{r}</li>)}
        </ul>
      )}

      {mayResolve && c.status === 'needs_review' && (
        deciding ? (
          <ImportDecision c={c} onDone={() => setDeciding(false)} />
        ) : (
          <div className="flex flex-wrap gap-2">
            {c.duplicate_of_trip_id && (
              <>
                <button
                  type="button"
                  disabled={resolve.isPending}
                  onClick={() => resolve.mutate({ dealId: c.zoho_deal_id, action: 'link', tripId: c.duplicate_of_trip_id })}
                  className={`${btn} bg-primary hover:bg-primary-hover text-text-inverse`}
                >
                  Same visit — link to that trip
                </button>
                <Link to={`/trips?trip=${c.duplicate_of_trip_id}`} className={`${btn} bg-surface-3 hover:bg-surface-strong text-text inline-flex items-center`}>
                  Open that trip
                </Link>
              </>
            )}
            <button
              type="button"
              onClick={() => setDeciding(true)}
              className={`${btn} ${c.duplicate_of_trip_id ? 'bg-surface-3 hover:bg-surface-strong text-text' : 'bg-primary hover:bg-primary-hover text-text-inverse'}`}
            >
              {c.duplicate_of_trip_id ? 'Import as a separate visit…' : 'Import as a trip…'}
            </button>
            <button type="button" disabled={resolve.isPending} onClick={() => resolve.mutate({ dealId: c.zoho_deal_id, action: 'ignore' })} className={`${btn} bg-surface-3 hover:bg-surface-strong text-text`}>
              Not a trip
            </button>
          </div>
        )
      )}
      {mayResolve && (c.status === 'ignored' || c.status === 'skipped') && (
        <button type="button" disabled={resolve.isPending} onClick={() => resolve.mutate({ dealId: c.zoho_deal_id, action: 'reopen' })} className="text-xs text-primary hover:underline min-h-[32px]">
          Move to review
        </button>
      )}
      {resolve.error && <p role="alert" className="text-xs text-notification">{(resolve.error as Error).message}</p>}
    </li>
  );
}

function ImportDecision({ c, onDone }: { c: DealCandidate; onDone: () => void }) {
  const { data: locations } = useAllLocations();
  const resolve = useResolveCandidate();
  const [loc, setLoc] = useState(c.location_id ?? '');
  const [start, setStart] = useState(c.visit_start ?? c.closing_date ?? '');
  const [end, setEnd] = useState(c.visit_end ?? c.visit_start ?? c.closing_date ?? '');

  // The deal's own account first, then everything else.
  const options = useMemo(() => {
    const all = [...(locations ?? [])].sort((a, b) => a.name.localeCompare(b.name));
    const mine = all.filter((l) => c.customer_id && l.customer_id === c.customer_id);
    return { mine, rest: all.filter((l) => !mine.includes(l)) };
  }, [locations, c.customer_id]);

  const invalid = !loc || !start || (end !== '' && end < start);

  return (
    <form
      className="space-y-2 rounded-lg border border-border bg-surface-2 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (invalid) return;
        resolve.mutate(
          { dealId: c.zoho_deal_id, action: 'import', locationId: loc, visitStart: start, visitEnd: end || start },
          { onSuccess: onDone },
        );
      }}
    >
      <label className="block text-xs text-text-muted space-y-1">
        <span>Which property was visited?</span>
        <select value={loc} onChange={(e) => setLoc(e.target.value)} className={inputClass}>
          <option value="">Choose a property…</option>
          {options.mine.length > 0 && (
            <optgroup label={c.account_name ?? 'This account'}>
              {options.mine.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </optgroup>
          )}
          <optgroup label="All properties">
            {options.rest.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </optgroup>
        </select>
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs text-text-muted space-y-1">
          <span>First day</span>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className={inputClass} />
        </label>
        <label className="text-xs text-text-muted space-y-1">
          <span>Last day</span>
          <input type="date" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} className={inputClass} />
        </label>
      </div>
      <p className="text-xs text-text-faint">
        A deal that covered several properties imports as one of them. Import the others from their own deals, or plan them as a run.
      </p>
      {resolve.error && <p role="alert" className="text-xs text-notification">{(resolve.error as Error).message}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onDone} className={`${btn} bg-surface-3 hover:bg-surface-strong text-text`}>Cancel</button>
        <button type="submit" disabled={invalid || resolve.isPending} className={`${btn} bg-primary hover:bg-primary-hover text-text-inverse`}>
          {resolve.isPending ? 'Importing…' : 'Import trip'}
        </button>
      </div>
    </form>
  );
}
